/**
 * WorkspaceProvider — resolves and exposes the workspace currently in effect
 * for the signed-in user.
 *
 * Resolution order:
 *   1. If a preview workspace is set (via `previewWorkspace`), use it. Used by
 *      the editor's live-preview pane; never persisted.
 *   2. If the user has suspended the workspace for this session ("View full
 *      console"), return null and render the unscoped layout.
 *   3. Otherwise, look up the user's role and pick the matching default
 *      workspace from the cloud query.
 *
 * Permissions are intersected on top: items the user lacks `view` on are
 * filtered out *after* the workspace's nav list is resolved. Workspaces never
 * widen access — that remains the RBAC system's job.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useCurrentUserRole } from "@/hooks/rbac/useCurrentUserRole";
import { useCurrentUserPermissions } from "@/hooks/rbac/useUserPermissions";
import { listWorkspaces } from "@/workspace/cloud";
import { pickWorkspaceForRole } from "@/workspace/pickWorkspace";
import {
  DASHBOARD_WIDGET_REGISTRY,
  NAV_ITEM_REGISTRY,
  type NavItemEntry,
  type WidgetEntry,
} from "@/workspace/registry";
import type { NavItemConfig, WidgetConfig, WorkspaceView } from "@/workspace/types";

const SUSPENSION_KEY = "smplify_workspace_suspended";

export interface ResolvedNavItem extends NavItemEntry {
  /** Effective label after workspace overrides. */
  label: string;
  /** Source position from the workspace, or 0 if unscoped. */
  order: number;
}

export interface ResolvedWidget extends WidgetEntry {
  order: number;
}

interface WorkspaceContextValue {
  /** The workspace actually in effect, or null if unscoped. */
  workspace: WorkspaceView | null;
  /** Whether resolution is still in flight. */
  isLoading: boolean;
  /** Sidebar items the user should see (workspace ∩ permissions). */
  effectiveNavItems: ResolvedNavItem[] | null;
  /** Dashboard widgets the user should see. */
  effectiveWidgets: ResolvedWidget[] | null;
  /** Default query string for a route, or undefined if none. */
  getPageDefault: (path: string) => string | undefined;
  /** Suspend the workspace for the rest of the browser session. */
  suspendForSession: () => void;
  /** Lift the suspension. */
  resumeFromSuspension: () => void;
  isSuspended: boolean;
  /** Set a preview workspace (in-memory). Pass null to clear. */
  previewWorkspace: (draft: WorkspaceView | null) => void;
}

const WorkspaceContext = React.createContext<WorkspaceContextValue>({
  workspace: null,
  isLoading: false,
  effectiveNavItems: null,
  effectiveWidgets: null,
  getPageDefault: () => undefined,
  suspendForSession: () => {},
  resumeFromSuspension: () => {},
  isSuspended: false,
  previewWorkspace: () => {},
});

export function useWorkspace(): WorkspaceContextValue {
  return React.useContext(WorkspaceContext);
}

function readSuspended(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(SUSPENSION_KEY) === "1";
  } catch {
    return false;
  }
}

function permitted(
  resource: string | undefined,
  perms: Record<string, string> | undefined,
): boolean {
  if (!resource) return true;
  if (!perms) return false;
  if (perms["*"] === "edit" || perms["*"] === "view") return true;
  const lvl = perms[resource];
  return lvl === "view" || lvl === "edit";
}

function buildEffectiveNav(
  workspace: WorkspaceView,
  perms: Record<string, string> | undefined,
): ResolvedNavItem[] {
  return workspace.navItems
    .map<ResolvedNavItem | null>((cfg: NavItemConfig) => {
      const entry = NAV_ITEM_REGISTRY[cfg.id as keyof typeof NAV_ITEM_REGISTRY] as
        | NavItemEntry
        | undefined;
      if (!entry) return null;
      if (!permitted(entry.permission, perms)) return null;
      return { ...entry, order: cfg.order, label: cfg.label?.trim() || entry.title };
    })
    .filter((x): x is ResolvedNavItem => x !== null)
    .sort((a, b) => a.order - b.order);
}

function buildEffectiveWidgets(workspace: WorkspaceView): ResolvedWidget[] {
  return workspace.dashboardWidgets
    .map<ResolvedWidget | null>((cfg: WidgetConfig) => {
      const entry =
        DASHBOARD_WIDGET_REGISTRY[cfg.id as keyof typeof DASHBOARD_WIDGET_REGISTRY];
      if (!entry) return null;
      return { ...entry, order: cfg.order };
    })
    .filter((x): x is ResolvedWidget => x !== null)
    .sort((a, b) => a.order - b.order);
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { data: roleName, isLoading: roleLoading } = useCurrentUserRole();
  const { data: perms } = useCurrentUserPermissions();
  const { data: workspaces, isLoading: wsLoading } = useQuery({
    queryKey: ["workspace_views"],
    queryFn: listWorkspaces,
    staleTime: 60_000,
  });

  const [isSuspended, setIsSuspended] = React.useState<boolean>(() => readSuspended());
  const [preview, setPreview] = React.useState<WorkspaceView | null>(null);

  const resolved = React.useMemo<WorkspaceView | null>(() => {
    if (preview) return preview;
    if (isSuspended) return null;
    return pickWorkspaceForRole(roleName ?? null, workspaces ?? []);
  }, [preview, isSuspended, roleName, workspaces]);

  const effectiveNavItems = React.useMemo(
    () => (resolved ? buildEffectiveNav(resolved, perms) : null),
    [resolved, perms],
  );

  const effectiveWidgets = React.useMemo(
    () => (resolved ? buildEffectiveWidgets(resolved) : null),
    [resolved],
  );

  const getPageDefault = React.useCallback(
    (path: string) => resolved?.pageDefaults?.[path],
    [resolved],
  );

  const suspendForSession = React.useCallback(() => {
    try {
      sessionStorage.setItem(SUSPENSION_KEY, "1");
    } catch {
      /* ignore */
    }
    setIsSuspended(true);
  }, []);

  const resumeFromSuspension = React.useCallback(() => {
    try {
      sessionStorage.removeItem(SUSPENSION_KEY);
    } catch {
      /* ignore */
    }
    setIsSuspended(false);
  }, []);

  const value = React.useMemo<WorkspaceContextValue>(
    () => ({
      workspace: resolved,
      isLoading: roleLoading || wsLoading,
      effectiveNavItems,
      effectiveWidgets,
      getPageDefault,
      suspendForSession,
      resumeFromSuspension,
      isSuspended,
      previewWorkspace: setPreview,
    }),
    [
      resolved,
      roleLoading,
      wsLoading,
      effectiveNavItems,
      effectiveWidgets,
      getPageDefault,
      suspendForSession,
      resumeFromSuspension,
      isSuspended,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
